import React, {useEffect} from "react";
import {Outlet} from "react-router";
import {InteractionType} from '@azure/msal-browser';
import {MsalAuthenticationTemplate} from "@azure/msal-react";
import i18n from "i18next";

import Grid from "@mui/material/Grid2";
import {createTheme, SelectChangeEvent, ThemeProvider, useTheme} from "@mui/material";
import * as locales from '@mui/material/locale';

import ApplicationBar from "../components/ApplicationBar";
import {isSupportedLocale} from "../util/LocalePreferences";
import type {SupportedAppLocale} from "../util/LocalePreferences";
import useLocale from "../hooks/useLocale";

type SupportedLocales = keyof typeof locales;

function getMuiLocale(locale: SupportedAppLocale): SupportedLocales {
    return locale.replace('-', '') as SupportedLocales;
}

export default function Root() {
    const theme = useTheme();
    const [locale, setLocale] = useLocale();
    const muiLocale = getMuiLocale(locale);

    useEffect(() => {
        i18n.changeLanguage(locale);
        document.title = 'CALI';
    }, [locale]);

    function handleLocaleChange(event: SelectChangeEvent<SupportedAppLocale>) {
        const selectedLocale = event.target.value;

        if (isSupportedLocale(selectedLocale)) {
            setLocale(selectedLocale);
        }
    }

    const localisedTheme = React.useMemo(
        () => createTheme(theme, locales[muiLocale]),
        [muiLocale, theme],
    );

    return (
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
            <ThemeProvider theme={localisedTheme}>
                <ApplicationBar locale={locale} onLocaleSwitched={handleLocaleChange} />
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Outlet/>
                    </Grid>
                </Grid>
            </ThemeProvider>
         </MsalAuthenticationTemplate>
    );
}
